/*
 * Designer-side model for the firmware's plate GPIO configuration.
 *
 * The wire format is intentionally kept identical to openHASP-next:
 *   gpio.config     -> eight packed uint32 GPIO slots
 *   gpio.entities   -> logical entities referring to those slots
 *   gpio.entity_schema -> entity schema revision
 *
 * `function_id` is Designer metadata only. It is removed before the payload
 * is sent to firmware; the firmware derives capabilities from domain/roles.
 */

export const GPIO_CONFIG_SLOTS = 8
export const GPIO_ENTITY_MAX = 8
export const GPIO_ENTITY_ID_MAX_LENGTH = 23
export const GPIO_ENTITY_SCHEMA = 2
export const GPIO_ADC_DEFAULTS = Object.freeze({
    raw_min: 0,
    raw_max: 4095,
    output_min: 0.0,
    output_max: 1.0,
    interval_ms: 100,
    change_threshold: 0.0,
    unit: '',
})

export const GPIO_FUNCTION = Object.freeze({
    OUTPUT: 1,
    INTERNAL_PULLUP: 2,
    INTERNAL_PULLDOWN: 3,
    EXTERNAL_PULLUP: 4,
    EXTERNAL_PULLDOWN: 5,
})

export const GPIO_TYPE = Object.freeze({
    FREE: 0x00,
    USED: 0x01,
    LED: 0x02,
    LED_R: 0x03,
    LED_G: 0x04,
    LED_B: 0x05,
    LED_CW: 0x06,
    LED_WW: 0x07,
    LED_W: 0x08,
    LIGHT_RELAY: 0x0a,
    POWER_RELAY: 0x0b,
    PWM: 0x21,
    SWITCH: 0xa0,
    BUTTON: 0xf0,
    HASP_ADC: 0xf9,
})

export const GPIO_TYPE_OPTIONS = Object.freeze([
    { value: GPIO_TYPE.POWER_RELAY, label: 'Power relay', kind: 'output' },
    { value: GPIO_TYPE.LIGHT_RELAY, label: 'Light relay', kind: 'output' },
    { value: GPIO_TYPE.PWM, label: 'PWM', kind: 'output' },
    { value: GPIO_TYPE.LED, label: 'LED', kind: 'output' },
    { value: GPIO_TYPE.LED_R, label: 'LED red', kind: 'output' },
    { value: GPIO_TYPE.LED_G, label: 'LED green', kind: 'output' },
    { value: GPIO_TYPE.LED_B, label: 'LED blue', kind: 'output' },
    { value: GPIO_TYPE.LED_CW, label: 'LED cool white', kind: 'output' },
    { value: GPIO_TYPE.LED_WW, label: 'LED warm white', kind: 'output' },
    { value: GPIO_TYPE.LED_W, label: 'LED white', kind: 'output' },
    { value: GPIO_TYPE.SWITCH, label: 'Digital input', kind: 'input' },
    { value: GPIO_TYPE.BUTTON, label: 'Button input', kind: 'input' },
    { value: GPIO_TYPE.HASP_ADC, label: 'ADC sensor', kind: 'adc' },
])

export const GPIO_PULL_OPTIONS = Object.freeze([
    { value: GPIO_FUNCTION.OUTPUT, label: 'Output' },
    { value: GPIO_FUNCTION.INTERNAL_PULLUP, label: 'Internal pull-up' },
    { value: GPIO_FUNCTION.INTERNAL_PULLDOWN, label: 'Internal pull-down' },
    { value: GPIO_FUNCTION.EXTERNAL_PULLUP, label: 'External pull-up' },
    { value: GPIO_FUNCTION.EXTERNAL_PULLDOWN, label: 'External pull-down' },
])

const output = (role, type) => ({ role, type, gpioFunction: GPIO_FUNCTION.OUTPUT })
const input = (role, type = GPIO_TYPE.SWITCH) => ({ role, type, gpioFunction: GPIO_FUNCTION.INTERNAL_PULLUP })

export const GPIO_ENTITY_FUNCTIONS = Object.freeze([
    {
        id: 'digital_input',
        label: 'Switch',
        domain: 'binary_sensor',
        description: 'One switch input exposing a read-only state.',
        properties: ['state'],
        events: ['on', 'off'],
        channels: [input('state')],
    },
    {
        id: 'digital_output',
        label: 'Digital Output',
        domain: 'switch',
        description: 'One generic digital output.',
        properties: ['state'],
        events: [],
        channels: [output('state', GPIO_TYPE.POWER_RELAY)],
    },
    {
        id: 'relay',
        label: 'Relay',
        domain: 'switch',
        description: 'One relay output using the firmware relay driver.',
        properties: ['state'],
        events: [],
        channels: [output('state', GPIO_TYPE.LIGHT_RELAY)],
    },
    {
        id: 'pwm_light',
        label: 'Light',
        domain: 'light',
        description: 'One PWM channel with firmware brightness/state semantics.',
        properties: ['state', 'brightness'],
        events: [],
        channels: [output('brightness', GPIO_TYPE.PWM)],
    },
    {
        id: 'rgb_light',
        label: 'RGB Light',
        domain: 'light',
        description: 'Three LED channels controlled by the firmware moodlight.',
        properties: ['state', 'brightness', 'rgb_color'],
        events: [],
        channels: [
            output('red', GPIO_TYPE.LED_R),
            output('green', GPIO_TYPE.LED_G),
            output('blue', GPIO_TYPE.LED_B),
        ],
    },
    {
        id: 'rgbww_light',
        label: 'RGBWW Light',
        domain: 'light',
        description: 'RGB plus cool-white and warm-white moodlight channels.',
        properties: ['state', 'brightness', 'rgb_color'],
        events: [],
        channels: [
            output('red', GPIO_TYPE.LED_R),
            output('green', GPIO_TYPE.LED_G),
            output('blue', GPIO_TYPE.LED_B),
            output('cool_white', GPIO_TYPE.LED_CW),
            output('warm_white', GPIO_TYPE.LED_WW),
        ],
    },
    {
        id: 'adc_sensor',
        label: 'ADC Sensor',
        domain: 'sensor',
        description: 'One read-only numeric value sampled and scaled by the firmware.',
        properties: ['value'],
        events: [],
        channels: [input('value', GPIO_TYPE.HASP_ADC)],
        adc: true,
    },
])

export const GPIO_SLOT_FUNCTIONS = Object.freeze([
    { id: 'digital_input', label: 'Switch', type: GPIO_TYPE.SWITCH, channels: ['state'] },
    { id: 'digital_output', label: 'Digital Output', type: GPIO_TYPE.POWER_RELAY, channels: ['state'] },
    { id: 'relay', label: 'Relay', type: GPIO_TYPE.LIGHT_RELAY, channels: ['state'] },
    { id: 'pwm_light', label: 'Light', type: GPIO_TYPE.PWM, channels: ['brightness'] },
    { id: 'rgb_light', label: 'RGB Light', type: 'rgb', channels: ['red', 'green', 'blue'] },
    { id: 'rgbww_light', label: 'RGBWW Light', type: 'rgbww', channels: ['red', 'green', 'blue', 'warm_white', 'cool_white'] },
    { id: 'adc_sensor', label: 'ADC Sensor', type: GPIO_TYPE.HASP_ADC, channels: ['value'] },
])

const functionMap = new Map(GPIO_ENTITY_FUNCTIONS.map(item => [item.id, item]))

export function getGpioEntityFunction(id) {
    return functionMap.get(id) ?? null
}

export function cloneGpioConfig(value) {
    return JSON.parse(JSON.stringify(value ?? createEmptyGpioConfig()))
}

export function unpackGpioSlot(word = 0) {
    const packed = Number(word) >>> 0
    return {
        pin: packed & 0xff,
        group: (packed >>> 8) & 0xff,
        type: (packed >>> 16) & 0xff,
        gpioFunction: (packed >>> 24) & 0x7f,
        inverted: Boolean((packed >>> 31) & 0x01),
    }
}

export function packGpioSlot(slot = {}) {
    const pin = Number.isInteger(slot.pin) ? slot.pin : 0
    const group = Number.isInteger(slot.group) ? slot.group : 0
    const type = Number.isInteger(slot.type) ? slot.type : GPIO_TYPE.FREE
    if (type === GPIO_TYPE.FREE) return 0
    const gpioFunction = Number.isInteger(slot.gpioFunction) ? slot.gpioFunction : GPIO_FUNCTION.OUTPUT
    const inverted = slot.inverted ? 1 : 0
    return ((pin & 0xff) | ((group & 0xff) << 8) | ((type & 0xff) << 16) |
        ((gpioFunction & 0x7f) << 24) | (inverted << 31)) >>> 0
}

export function emptyGpioSlot() {
    return { pin: null, group: 0, type: GPIO_TYPE.FREE, gpioFunction: GPIO_FUNCTION.OUTPUT, inverted: false, function_id: null, channel: null }
}

export function createEmptyGpioConfig() {
    return {
        entity_schema: GPIO_ENTITY_SCHEMA,
        slots: Array.from({ length: GPIO_CONFIG_SLOTS }, emptyGpioSlot),
        entities: [],
    }
}

function deriveCapabilities(entity) {
    const roles = new Set((entity.channels ?? []).map(channel => channel.role))
    const capabilities = []
    if (['light', 'switch', 'binary_sensor'].includes(entity.domain)) capabilities.push('state')
    if (entity.domain === 'light' && (roles.has('brightness') || ['red', 'green', 'blue', 'cool_white', 'warm_white', 'white'].some(role => roles.has(role)))) {
        capabilities.push('brightness')
    }
    if (entity.domain === 'light' && ['red', 'green', 'blue', 'cool_white', 'warm_white', 'white'].some(role => roles.has(role))) {
        capabilities.push('rgb_color')
    }
    if (entity.domain === 'sensor' && roles.has('value')) capabilities.push('value')
    return capabilities
}

function normalizeEntity(entity) {
    const channels = (entity?.channels ?? []).map(channel => ({
        role: channel.role,
        slot: Number.isInteger(channel.slot) ? channel.slot : Number(channel.slot),
    }))
    const normalized = {
        id: entity?.id ?? '',
        name: entity?.name ?? entity?.id ?? '',
        domain: entity?.domain ?? '',
        function_id: entity?.function_id ?? entity?.function ?? null,
        capabilities: entity?.capabilities ?? deriveCapabilities({ ...entity, channels }),
        channels,
    }
    if (normalized.domain === 'sensor' || normalized.function_id === 'adc_sensor') {
        normalized.adc = { ...GPIO_ADC_DEFAULTS, ...(entity?.adc ?? {}) }
    }
    return normalized
}

const typeToChannel = Object.freeze({
    [GPIO_TYPE.SWITCH]: 'state',
    [GPIO_TYPE.BUTTON]: 'state',
    [GPIO_TYPE.POWER_RELAY]: 'state',
    [GPIO_TYPE.LIGHT_RELAY]: 'state',
    [GPIO_TYPE.PWM]: 'brightness',
    [GPIO_TYPE.LED_R]: 'red',
    [GPIO_TYPE.LED_G]: 'green',
    [GPIO_TYPE.LED_B]: 'blue',
    [GPIO_TYPE.LED_CW]: 'cool_white',
    [GPIO_TYPE.LED_WW]: 'warm_white',
    [GPIO_TYPE.LED_W]: 'white',
    [GPIO_TYPE.HASP_ADC]: 'value',
})

export function gpioSlotChannel(slot, entities = []) {
    if (slot?.channel) return slot.channel
    const linked = entities.flatMap(entity => entity.channels ?? [])
        .find(channel => channel.slot === slot?.index)
    return linked?.role ?? typeToChannel[slot?.type] ?? null
}

export function inferGpioSlotFunction(slot, entities = []) {
    if (slot?.function_id && getGpioEntityFunction(slot.function_id)) return slot.function_id
    if (slot?.type === GPIO_TYPE.LED_CW || slot?.type === GPIO_TYPE.LED_WW) return 'rgbww_light'
    if ([GPIO_TYPE.LED_R, GPIO_TYPE.LED_G, GPIO_TYPE.LED_B].includes(slot?.type)) {
        const linked = entities.find(entity => (entity.channels ?? []).some(channel => channel.slot === slot?.index))
        if (linked?.function_id === 'rgbww_light' || linked?.domain === 'light' && (linked.channels ?? []).some(channel => ['cool_white', 'warm_white'].includes(channel.role))) return 'rgbww_light'
        return 'rgb_light'
    }
    if (slot?.type === GPIO_TYPE.HASP_ADC) return 'adc_sensor'
    if (slot?.type === GPIO_TYPE.PWM) return 'pwm_light'
    if (slot?.type === GPIO_TYPE.LIGHT_RELAY) return 'relay'
    if (slot?.type === GPIO_TYPE.POWER_RELAY) return 'digital_output'
    if (slot?.type === GPIO_TYPE.SWITCH || slot?.type === GPIO_TYPE.BUTTON) return 'digital_input'
    return null
}

export function gpioSlotFunctionDefinition(slot, entities = []) {
    return GPIO_SLOT_FUNCTIONS.find(item => item.id === inferGpioSlotFunction(slot, entities)) ?? null
}

export function gpioSlotTypeLabel(slot) {
    if ([GPIO_TYPE.SWITCH, GPIO_TYPE.BUTTON].includes(slot?.type)) return 'Digital Input'
    if (slot?.type === GPIO_TYPE.HASP_ADC) return 'Analog Input'
    if (slot?.type === GPIO_TYPE.PWM || [GPIO_TYPE.LED, GPIO_TYPE.LED_R, GPIO_TYPE.LED_G, GPIO_TYPE.LED_B, GPIO_TYPE.LED_CW, GPIO_TYPE.LED_WW, GPIO_TYPE.LED_W].includes(slot?.type)) return 'PWM Output'
    if ([GPIO_TYPE.POWER_RELAY, GPIO_TYPE.LIGHT_RELAY].includes(slot?.type)) return 'Digital Output'
    return slotTypeLabel(slot?.type)
}

export function gpioTypeForSlotFunction(functionId, channel = null) {
    if (functionId === 'rgb_light' || functionId === 'rgbww_light') {
        return {
            red: GPIO_TYPE.LED_R,
            green: GPIO_TYPE.LED_G,
            blue: GPIO_TYPE.LED_B,
            warm_white: GPIO_TYPE.LED_WW,
            cool_white: GPIO_TYPE.LED_CW,
        }[channel] ?? GPIO_TYPE.LED_R
    }
    return GPIO_SLOT_FUNCTIONS.find(item => item.id === functionId)?.type ?? GPIO_TYPE.FREE
}

export function gpioSlotFunctionChannels(functionId) {
    return GPIO_SLOT_FUNCTIONS.find(item => item.id === functionId)?.channels ?? []
}

export function inferGpioEntityFunction(entity, slots = []) {
    const channels = entity?.channels ?? []
    if (!channels.length && entity?.function_id && getGpioEntityFunction(entity.function_id)) return entity.function_id
    const roles = new Set(channels.map(channel => channel.role))
    const channelTypes = channels.map(channel => slots[channel.slot]?.type)
    if (roles.has('value') && channelTypes.includes(GPIO_TYPE.HASP_ADC)) return 'adc_sensor'
    if (roles.has('cool_white') || roles.has('warm_white')) return 'rgbww_light'
    if (roles.has('red') || roles.has('green') || roles.has('blue')) return 'rgb_light'
    if (roles.has('brightness') && channelTypes.includes(GPIO_TYPE.PWM)) return 'pwm_light'
    if (roles.has('state') && channelTypes.length === 1) {
        if (channelTypes[0] === GPIO_TYPE.LIGHT_RELAY) return 'relay'
        if (channelTypes[0] === GPIO_TYPE.POWER_RELAY) return 'digital_output'
        if ([GPIO_TYPE.SWITCH, GPIO_TYPE.BUTTON].includes(channelTypes[0])) return 'digital_input'
    }
    if (entity?.domain === 'binary_sensor') return 'digital_input'
    if (entity?.domain === 'switch') {
        const type = slots[channels.find(channel => channel.role === 'state')?.slot]?.type
        return type === GPIO_TYPE.LIGHT_RELAY ? 'relay' : 'digital_output'
    }
    if (entity?.domain === 'light') {
        if (roles.has('cool_white') || roles.has('warm_white')) return 'rgbww_light'
        if (roles.has('red') || roles.has('green') || roles.has('blue')) return 'rgb_light'
        if (roles.has('brightness')) return 'pwm_light'
    }
    if (entity?.domain === 'sensor' && roles.has('value')) return 'adc_sensor'
    return null
}

export function normalizeGpioConfig(raw) {
    const source = raw?.gpio ?? raw ?? {}
    const packedSlots = Array.isArray(source.config) ? source.config : []
    const slots = Array.from({ length: GPIO_CONFIG_SLOTS }, (_, index) => {
        const value = packedSlots[index]
        if (Array.isArray(source.slots) && source.slots[index]) return { ...emptyGpioSlot(), ...source.slots[index], index }
        if (value === undefined || value === null) return { ...emptyGpioSlot(), index }
        return { ...unpackGpioSlot(value), index }
    })
    const entities = Array.isArray(source.entities) ? source.entities.map(entity => normalizeEntity(entity)) : []
    for (const entity of entities) entity.function_id = inferGpioEntityFunction(entity, slots)
    slots.forEach(slot => {
        slot.function_id = inferGpioSlotFunction(slot, entities)
        slot.channel = gpioSlotChannel(slot, entities)
    })
    return {
        entity_schema: Number(source.entity_schema) || GPIO_ENTITY_SCHEMA,
        slots,
        entities,
    }
}

export function serializeGpioConfig(model) {
    const normalized = normalizeGpioConfig(model)
    return {
        config: normalized.slots.map(packGpioSlot),
        entity_schema: GPIO_ENTITY_SCHEMA,
        entities: normalized.entities.map(entity => {
            const result = {
                id: entity.id,
                name: entity.name || entity.id,
                domain: entity.domain,
                // The current firmware derives sensor.value internally but its
                // schema-2 parser rejects "value" in the supplied capabilities.
                // Omit that optional field for ADC; read-back and descriptors
                // still expose the read-only value capability.
                capabilities: entity.domain === 'sensor' ? [] : deriveCapabilities(entity),
                channels: entity.channels.map(channel => ({ role: channel.role, slot: channel.slot })),
            }
            if (entity.domain === 'sensor') result.adc = { ...GPIO_ADC_DEFAULTS, ...(entity.adc ?? {}) }
            return result
        }),
    }
}

export function createEntityFromFunction(functionId, id = '') {
    const definition = getGpioEntityFunction(functionId) ?? GPIO_ENTITY_FUNCTIONS[0]
    return {
        id,
        name: id,
        function_id: definition.id,
        domain: definition.domain,
        capabilities: [...definition.properties],
        channels: definition.channels.map(channel => ({ role: channel.role, slot: null })),
        ...(definition.adc ? { adc: { ...GPIO_ADC_DEFAULTS } } : {}),
    }
}

function isInputType(type) {
    return type >= 0x80 && type !== 0xff
}

function isAnalogType(type) {
    return (type >= GPIO_TYPE.LED && type <= GPIO_TYPE.LED_W) || type === GPIO_TYPE.PWM
}

function roleMatchesType(role, type) {
    if (role === 'state') return type > GPIO_TYPE.USED && type < 0x80 || isInputType(type)
    if (role === 'brightness') return isAnalogType(type)
    return {
        red: GPIO_TYPE.LED_R,
        green: GPIO_TYPE.LED_G,
        blue: GPIO_TYPE.LED_B,
        cool_white: GPIO_TYPE.LED_CW,
        warm_white: GPIO_TYPE.LED_WW,
        white: GPIO_TYPE.LED_W,
        value: GPIO_TYPE.HASP_ADC,
    }[role] === type
}

export const GPIO_HARDWARE_PROFILES = Object.freeze({
    'esp32-s3': Object.freeze({ id: 'esp32-s3', adcPins: { min: 1, max: 10 } }),
    'darwin-sdl': Object.freeze({ id: 'darwin-sdl', adcPins: { min: 0, max: 39 } }),
})

export function getGpioHardwareProfile(plate, appSettings) {
    const id = plate?.gpio_board_profile ?? plate?.board_profile ?? appSettings?.gpio_board_profile ?? appSettings?.board_profile
    return typeof id === 'string' ? GPIO_HARDWARE_PROFILES[id.toLowerCase()] ?? null : null
}

function validateAdcConfig(entity, errors) {
    const adc = { ...GPIO_ADC_DEFAULTS, ...(entity.adc ?? {}) }
    if (!Number.isInteger(adc.raw_min) || adc.raw_min < 0 || adc.raw_min > 65535) errors.push(`Entity '${entity.id}': raw_min moet een geheel getal van 0–65535 zijn.`)
    if (!Number.isInteger(adc.raw_max) || adc.raw_max < 0 || adc.raw_max > 65535) errors.push(`Entity '${entity.id}': raw_max moet een geheel getal van 0–65535 zijn.`)
    if (Number.isInteger(adc.raw_min) && Number.isInteger(adc.raw_max) && adc.raw_min >= adc.raw_max) errors.push(`Entity '${entity.id}': raw_min en raw_max mogen niet gelijk zijn en raw_min moet kleiner zijn.`)
    if (!Number.isFinite(adc.output_min) || !Number.isFinite(adc.output_max)) errors.push(`Entity '${entity.id}': output_min en output_max moeten numeriek geldig zijn.`)
    if (!Number.isInteger(adc.interval_ms) || adc.interval_ms < 10 || adc.interval_ms > 60000) errors.push(`Entity '${entity.id}': interval_ms moet 10–60000 ms zijn.`)
    if (!Number.isFinite(adc.change_threshold) || adc.change_threshold < 0) errors.push(`Entity '${entity.id}': change_threshold moet een niet-negatief getal zijn.`)
    if (typeof adc.unit !== 'string' || adc.unit.length >= 16) errors.push(`Entity '${entity.id}': unit mag maximaal 15 tekens bevatten.`)
}

export function validateGpioPlateConfig(model, { hardwareProfile = null } = {}) {
    const errors = []
    const warnings = []
    const slots = model?.slots ?? []
    const entities = model?.entities ?? []
    if (slots.length !== GPIO_CONFIG_SLOTS) errors.push(`Firmware verwacht exact ${GPIO_CONFIG_SLOTS} GPIO-slots.`)
    const pins = new Map()
    slots.forEach((slot, index) => {
        if (slot.type === GPIO_TYPE.FREE) return
        if (!Number.isInteger(slot.pin) || slot.pin < 0 || slot.pin > 255) errors.push(`Slot ${index}: kies een geldige GPIO-pin.`)
        else if (pins.has(slot.pin)) errors.push(`GPIO-pin ${slot.pin} is dubbel toegewezen aan slot ${pins.get(slot.pin)} en ${index}.`)
        else pins.set(slot.pin, index)
        if (!Number.isInteger(slot.group) || slot.group < 0 || slot.group > 255) errors.push(`Slot ${index}: group moet 0–255 zijn.`)
        const typeDefinition = GPIO_TYPE_OPTIONS.find(option => option.value === slot.type)
        if (typeDefinition?.kind === 'output' && slot.gpioFunction !== GPIO_FUNCTION.OUTPUT) errors.push(`Slot ${index}: een outputtype vereist GPIO function Output.`)
        if (['input', 'adc'].includes(typeDefinition?.kind) && slot.gpioFunction === GPIO_FUNCTION.OUTPUT) errors.push(`Slot ${index}: een inputtype vereist een pull-configuratie.`)
        if (slot.type === GPIO_TYPE.HASP_ADC && hardwareProfile?.adcPins &&
            (slot.pin < hardwareProfile.adcPins.min || slot.pin > hardwareProfile.adcPins.max)) {
            errors.push(`Slot ${index}: GPIO ${slot.pin} is geen ADC-pin voor ${hardwareProfile.id}.`)
        }
        if (slot.type === GPIO_TYPE.HASP_ADC && !hardwareProfile) {
            warnings.push(`Slot ${index}: ADC-pincapability wordt door de firmware gecontroleerd omdat geen boardprofiel bekend is.`)
        }
    })
    if (entities.length > GPIO_ENTITY_MAX) errors.push(`Firmware ondersteunt maximaal ${GPIO_ENTITY_MAX} logische entities.`)
    const ids = new Set()
    const usedSlots = new Map()
    entities.forEach((entity, index) => {
        if (!/^[A-Za-z_][A-Za-z0-9_-]{0,22}$/.test(entity.id ?? '')) errors.push(`Entity ${index + 1}: ID moet firmware-compatibel zijn (letter/underscore, maximaal 23 tekens).`)
        if (ids.has(entity.id)) errors.push(`Entity-ID '${entity.id}' is niet uniek.`)
        ids.add(entity.id)
        const inferredFunction = inferGpioEntityFunction(entity, slots)
        const definition = getGpioEntityFunction(inferredFunction) ?? getGpioEntityFunction(entity.function_id)
        if (!definition) {
            errors.push(`Entity '${entity.id || index + 1}': functie wordt niet door deze firmware ondersteund.`)
            return
        }
        if (entity.domain !== definition.domain) errors.push(`Entity '${entity.id}': domain komt niet overeen met functie ${definition.label}.`)
        if (entity.channels.length !== definition.channels.length) errors.push(`Entity '${entity.id}': functie ${definition.label} vereist ${definition.channels.length} kana(a)l(en).`)
        const roles = new Set()
        entity.channels.forEach(channel => {
            if (roles.has(channel.role)) errors.push(`Entity '${entity.id}': rol '${channel.role}' komt dubbel voor.`)
            roles.add(channel.role)
            const slot = slots[channel.slot]
            if (!slot || slot.type === GPIO_TYPE.FREE) errors.push(`Entity '${entity.id}': kanaal '${channel.role}' verwijst naar een lege slot.`)
            else if (!roleMatchesType(channel.role, slot.type)) errors.push(`Entity '${entity.id}': GPIO-slot ${channel.slot} past niet bij rol '${channel.role}'.`)
            if (usedSlots.has(channel.slot)) errors.push(`GPIO-slot ${channel.slot} wordt door meerdere entities gebruikt.`)
            else usedSlots.set(channel.slot, entity.id)
        })
        const requiredRoles = definition.channels.map(channel => channel.role)
        requiredRoles.filter(role => !roles.has(role)).forEach(role => errors.push(`Entity '${entity.id}': kanaalrol '${role}' ontbreekt.`))
        if (definition.id === 'adc_sensor') validateAdcConfig(entity, errors)
    })
    if (!entities.length && slots.some(slot => slot.type !== GPIO_TYPE.FREE)) warnings.push('Er zijn fysieke GPIO-slots ingesteld zonder logische entity.')
    return { valid: errors.length === 0, errors, warnings }
}

export function slotTypeLabel(type) {
    return type === GPIO_TYPE.FREE ? 'Free' : GPIO_TYPE_OPTIONS.find(option => option.value === type)?.label ?? `Firmware type ${type}`
}

export function gpioPropertiesForEntity(entity) {
    const definition = getGpioEntityFunction(entity?.function_id) ?? getGpioEntityFunction(inferGpioEntityFunction(entity))
    return definition?.properties ?? []
}

/**
 * The entity descriptor the firmware publishes (retained MQTT
 * state/gpio/entities, hasp_gpio_entity.cpp gpio_entity_get_descriptor) for a
 * plate GPIO configuration. Lets the Designer use a locally stored plate
 * configuration exactly like a live descriptor. `config_revision` is 0: the
 * revision only exists on the device. Returns null without entities.
 */
export function gpioDescriptorFromConfig(raw) {
    if (!raw) return null
    const { entities } = normalizeGpioConfig(raw)
    const usable = entities.filter(entity => entity.id && entity.domain)
    if (usable.length === 0) return null
    return {
        config_revision: 0,
        entities: usable.map(entity => {
            const capabilities = new Set([...(entity.capabilities ?? []), ...deriveCapabilities(entity)])
            const properties = {}
            if (capabilities.has('state')) {
                properties.state = { type: 'boolean', writable: entity.domain !== 'binary_sensor' }
            }
            if (capabilities.has('brightness')) {
                properties.brightness = { type: 'integer', min: 0, max: 255, writable: true }
            }
            if (capabilities.has('rgb_color')) {
                properties.rgb_color = { type: 'array', length: 3, min: 0, max: 255, writable: true }
            }
            if (capabilities.has('value')) {
                const adc = { ...GPIO_ADC_DEFAULTS, ...(entity.adc ?? {}) }
                properties.value = { type: 'number', writable: false, min: adc.output_min, max: adc.output_max }
                if (adc.unit) properties.value.unit = adc.unit
            }
            return {
                id: entity.id,
                name: entity.name || entity.id,
                domain: entity.domain,
                // Like the firmware, `value` is a property but not an advertised capability.
                capabilities: ['state', 'brightness', 'rgb_color'].filter(capability => capabilities.has(capability)),
                properties,
            }
        }),
    }
}
